import { Cookies } from 'react-cookie';
import featureFlagsConfig from '../../helpers/featureFlags/featureFlags.json';

interface FlagStatus {
  featureName: string;
  isActive: boolean;
  releaseMode: string;
  version: number;
}

interface FeatureConfig {
  feature: string;
  releaseAll: boolean;
  releaseMode: string;
  canaryOptions: {
    exposure: number;
    version: number;
  };
}

type ReleaseConfiguration = typeof featureFlagsConfig;

const RELEASE_MODE_FEATURE_FLAG = 'FeatureFlag';
const RELEASE_MODE_CANARY = 'Canary';

class FeatureFlagsHelper {
  releaseConfig: ReleaseConfiguration = featureFlagsConfig;
  status: FlagStatus[] = [];
  ssrCookies: Record<string, string> = {};

  constructor() {
    this.load();
  }

  setServerSideCookies(cookies: Record<string, string>) {
    this.ssrCookies = cookies;
    this.load();
  }

  isActive(feature: string): boolean {
    const status = this.getFlag(feature);
    return status && status.isActive;
  }

  setFlag(feature: string, isActive: boolean) {
    // Get configuration from feature
    const featureConfig = this.releaseConfig.find((x) => x.feature === feature);
    if (!featureConfig) {
      return;
    }

    // Set browser cookie value
    this.setCookie(featureConfig, isActive);

    // Reload flags
    this.load();
  }

  load() {
    this.status = this.initFlagStatus(this.ssrCookies);
  }

  getFlags(): FlagStatus[] {
    return this.status;
  }

  getConfig(): ReleaseConfiguration {
    return this.releaseConfig;
  }

  private getFlag(feature: string): FlagStatus {
    return this.status.find((item) => {
      return item.featureName === feature;
    });
  }

  private initFlagStatus(ssrCookies: Record<string, string>) {
    const status: FlagStatus[] = [];

    // Initialize cookie client
    const cookies = new Cookies();

    this.releaseConfig.forEach((featureConfig) => {
      // Get the cookie name for this feature and version
      const cookieName = this.getCookieName(featureConfig);

      // If canary config exposeAll is set to true then skip cookie checking and enable feature
      if (featureConfig.releaseAll) {
        status.push(this.buildFlagStatus(featureConfig, true));
        return;
      }

      // Check server side cookie
      let isSsrCookieFound = false;
      if (ssrCookies) {
        const ssrCookieFlag = ssrCookies[cookieName];
        if (ssrCookieFlag) {
          isSsrCookieFound = true;
          status.push(
            this.buildFlagStatus(featureConfig, ssrCookieFlag === 'true'),
          );
        }
      }

      // Check browser cookie if server side cookie not found
      let isBrowserCookieFound = false;
      if (!isSsrCookieFound) {
        const cookieFlag = cookies.get(cookieName);
        if (cookieFlag) {
          isBrowserCookieFound = true;
          status.push(
            this.buildFlagStatus(featureConfig, String(cookieFlag) === 'true'),
          );
        }
      }

      // If cookie not found, roll chance and add to cookie
      if (!isSsrCookieFound && !isBrowserCookieFound) {
        if (featureConfig.releaseMode === RELEASE_MODE_FEATURE_FLAG) {
          // Default flag to null (feature not set) if feature not found in cookie
          status.push(this.buildFlagStatus(featureConfig, null));
        } else if (featureConfig.releaseMode === RELEASE_MODE_CANARY) {
          // Roll chance based on exposure percentage
          const isActive = this.rollChance(
            featureConfig.canaryOptions.exposure,
          );

          // Set browser cookie value
          this.setCookie(featureConfig, isActive);

          // Set status
          status.push(this.buildFlagStatus(featureConfig, isActive));
        } else {
          // do nothing
        }
      }
    });

    return status;
  }

  private buildFlagStatus(featureConfig: FeatureConfig, isActive: boolean) {
    return {
      featureName: featureConfig.feature,
      releaseMode: featureConfig.releaseMode,
      version: featureConfig.canaryOptions.version
        ? featureConfig.canaryOptions.version
        : 0,
      isActive: isActive,
    };
  }

  private setCookie(feature: FeatureConfig, isActive: boolean) {
    const cookieName = this.getCookieName(feature);
    if (cookieName) {
      const cookieValue = isActive ? 'true' : 'false';
      const expiryDate = new Date(
        new Date().setFullYear(new Date().getFullYear() + 1),
      );
      const cookies = new Cookies();
      cookies.set(cookieName, cookieValue, {
        path: '/',
        expires: expiryDate,
        sameSite: 'lax',
        secure: process.env.NODE_ENV === 'production',
      });
    }
  }

  private getCookieName(featureConfig: FeatureConfig): string {
    if (featureConfig.releaseMode === RELEASE_MODE_FEATURE_FLAG) {
      return `${featureConfig.feature}`;
    }
    if (featureConfig.releaseMode === RELEASE_MODE_CANARY) {
      return `Canary_${featureConfig.feature}_v${featureConfig.canaryOptions.version}`;
    }

    return null;
  }

  private rollChance(percentage: number): boolean {
    return Math.random() < percentage;
  }
}

export default new FeatureFlagsHelper();
